import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Copy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import { getPayoutMethods, getWallet, withdraw, hasPaymentPin } from "@/lib/monetizationStore";
import { useMoneVersion } from "@/lib/hooks";
import PinSheet from "@/components/PinSheet";

const Withdraw = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const v = useMoneVersion();

    const wallet = useMemo(() => (user ? getWallet(user.id) : { available: 0 }), [user, v]);
    const methods = useMemo(() => (user ? getPayoutMethods(user.id) : []), [user, v]);

    const [amount, setAmount] = useState("");
    const [pmId, setPmId] = useState(methods.find((m) => m.isPrimary)?.id || methods[0]?.id || "");
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pinMode, setPinMode] = useState(null); // null | "set" | "verify" | "reset"

    const selected = methods.find((m) => m.id === pmId);

    const runWithdrawal = () => {
        setBusy(true);
        setTimeout(() => {
            const res = withdraw(user.id, Number(amount), pmId);
            setBusy(false);
            if (!res.ok) setError(res.error);
            else setResult(res);
        }, 700);
    };

    const handleConfirm = () => {
        setError(null);
        // Basic validation before PIN
        if (!amount || Number(amount) <= 0) {
            setError("Enter an amount");
            return;
        }
        if (!pmId) {
            setError("Select a payout method");
            return;
        }
        // PIN gate: set if missing, otherwise verify
        if (!user) return;
        if (!hasPaymentPin(user.id)) setPinMode("set");
        else setPinMode("verify");
    };

    const handlePinSuccess = () => {
        setPinMode(null);
        runWithdrawal();
    };

    const handlePinClose = (opts) => {
        // If the user tapped "Forgot PIN", switch into reset flow
        if (opts && opts.forgot) {
            setPinMode("reset");
            return;
        }
        setPinMode(null);
    };

    if (result) {
        return (
            <div className="screen" data-testid="withdraw-success-screen">
                <PageHeader title="Withdrawal" />
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <span
                        className="h-20 w-20 rounded-full flex items-center justify-center text-primary-foreground shadow-card"
                        style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                    >
                        <CheckCircle2 size={36} />
                    </span>
                    <h2 className="mt-5 font-display text-[22px] font-semibold">Withdrawal queued</h2>
                    <p className="mt-1 text-[13px] text-muted-foreground max-w-xs">
                        ₦{Number(amount).toLocaleString()} to {selected?.bankName} · {selected?.accountName}
                    </p>
                    <div className="mt-5 rounded-2xl border border-border bg-card px-4 py-3 inline-flex items-center gap-2">
                        <span className="text-[11.5px] text-muted-foreground uppercase tracking-wider">
                            Ref
                        </span>
                        <span className="font-mono text-[12.5px] font-semibold" data-testid="withdraw-ref">
                            {result.reference}
                        </span>
                        <button
                            data-testid="withdraw-copy-ref"
                            onClick={() => navigator.clipboard?.writeText(result.reference)}
                            className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center"
                            aria-label="Copy"
                        >
                            <Copy size={13} />
                        </button>
                    </div>
                    <div className="mt-8 w-full max-w-xs">
                        <GradientButton
                            testId="withdraw-done"
                            onClick={() => navigate("/wallet", { replace: true })}
                        >
                            Back to Wallet
                        </GradientButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="screen" data-testid="withdraw-screen">
            <PageHeader title="Withdraw" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                <div className="rounded-2xl border border-border bg-card p-4">
                    <div className="text-[11.5px] uppercase tracking-wider text-muted-foreground">Available</div>
                    <div className="mt-1 font-display text-[22px] font-semibold">
                        ₦{wallet.available.toLocaleString()}
                    </div>
                </div>

                <label className="block mt-5">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">Amount</span>
                    <span className="flex items-center gap-2 h-14 px-4 rounded-2xl border border-border bg-secondary focus-within:bg-background focus-within:border-primary">
                        <span className="text-muted-foreground">₦</span>
                        <input
                            data-testid="withdraw-amount"
                            type="number"
                            inputMode="numeric"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0"
                            className="flex-1 bg-transparent outline-none text-[17px] font-semibold"
                        />
                        <button
                            data-testid="withdraw-max"
                            onClick={() => setAmount(String(wallet.available))}
                            className="text-[12px] font-semibold text-primary"
                        >
                            Max
                        </button>
                    </span>
                </label>

                <h3 className="mt-6 mb-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold ml-1">
                    Select bank
                </h3>
                {methods.length === 0 ? (
                    <div className="rounded-2xl border border-border bg-card p-4 text-center">
                        <p className="text-[13px] text-muted-foreground">No payout methods yet.</p>
                        <button
                            data-testid="withdraw-add-method"
                            onClick={() => navigate("/wallet/payouts")}
                            className="mt-3 h-11 px-5 rounded-full bg-primary text-primary-foreground font-medium text-[13px]"
                        >
                            Add bank account
                        </button>
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="withdraw-methods">
                        {methods.map((m) => (
                            <li key={m.id}>
                                <button
                                    data-testid={`withdraw-method-${m.id}`}
                                    onClick={() => setPmId(m.id)}
                                    className={[
                                        "w-full flex items-center gap-3 p-3 rounded-2xl border transition-colors text-left",
                                        pmId === m.id ? "border-primary bg-primary/5" : "border-border bg-card",
                                    ].join(" ")}
                                >
                                    <span
                                        className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-semibold text-[11px]"
                                        style={{ background: m.bankColor }}
                                    >
                                        {m.bankName.slice(0, 2).toUpperCase()}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13px] font-semibold truncate">{m.accountName}</div>
                                        <div className="text-[11.5px] text-muted-foreground truncate">
                                            {m.bankName} · {m.accountNumber}
                                        </div>
                                    </div>
                                    {pmId === m.id && <CheckCircle2 size={16} className="text-primary" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {error && (
                    <p data-testid="withdraw-error" className="mt-3 text-[12.5px] text-destructive">
                        {error}
                    </p>
                )}
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background">
                <GradientButton
                    testId="withdraw-confirm"
                    onClick={handleConfirm}
                    disabled={busy || !amount || !pmId}
                >
                    {busy ? "Processing…" : "Confirm withdrawal"}
                </GradientButton>
            </div>

            {pinMode && user && (
                <PinSheet
                    open={!!pinMode}
                    onClose={handlePinClose}
                    userId={user.id}
                    mode={pinMode}
                    onSuccess={handlePinSuccess}
                />
            )}
        </div>
    );
};

export default Withdraw;
